import {ChangeDetectionStrategy, Component} from '@angular/core';
import {CmsComponent, CmsService} from '@spartacus/core';
import {CmsComponentData} from '@spartacus/storefront';
import {combineLatest, Observable, of} from 'rxjs';
import {concatMap, distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';

import {AimoCMSSimpleBannerContainerComponent, AimoCMSTabbedContainerComponent} from '../../../model/cms.model';
import {AimoGtmService} from '../../../service/gtm/aimo-gtm.service';
import {AimoCMSCarouselComponentData} from '../carousel/cms/aimo-cms-carousel.component';
import {AimoProductSearchService} from '../../../service/product/search/aimo-product-search.service';
import {ActivatedRoute} from '@angular/router';
import {AimoAssortmentService} from '../../../service/product/aimo-assortment.service';

@Component({
    selector: 'aimo-tabbed-container',
    templateUrl: './aimo-tabbed-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AimoTabbedContainerComponent {
    componentData$: Observable<AimoCMSTabbedContainerComponent>;

    tabs$: Observable<CmsComponent[]>;
    activeTab: CmsComponent = null;
    showAsAccordion: boolean = false;
    accordionOpen = true;

    constructor(
        protected gtmService: AimoGtmService,
        protected activatedRoute: ActivatedRoute,
        protected productSearchService: AimoProductSearchService,
        protected component: CmsComponentData<AimoCMSSimpleBannerContainerComponent>,
        protected cmsService: CmsService,
        protected aimoAssortmentService: AimoAssortmentService,
    ) {
        this.componentData$ = this.component.data$.pipe(filter(Boolean));

        this.tabs$ = this.getTabs();
    }

    private getTabs(): Observable<CmsComponent[]> {
        return this.componentData$.pipe(
            filter((data) => data != null),
            tap((component) => {
                this.showAsAccordion = component.showAsAccordion === true || component.showAsAccordion === 'true';
            }),
            concatMap((data) => {
                return combineLatest(
                    data?.productCarousels?.split(' ').map((component) =>
                        combineLatest([
                            this.cmsService.getComponentData<AimoCMSCarouselComponentData>(component),
                            this.aimoAssortmentService.isOnlyMyAssortment().pipe(distinctUntilChanged((curr, prev) => curr === prev)),
                        ]).pipe(
                            switchMap(([component, ownAssortment]) =>
                                this.productSearchService.getProducts(
                                    component,
                                    false,
                                    {},
                                    this.activatedRoute,
                                    ownAssortment,
                                ),
                            ),
                            switchMap((products) =>
                                products?.length > 0
                                    ? this.cmsService.getComponentData<AimoCMSCarouselComponentData>(component)
                                    : of(undefined),
                            ),
                        ),
                    ) ?? [],
                ).pipe(map((components) => components.filter((component) => component?.uid !== undefined)));
            }),
        );
    }

    setActiveTab(tab: CmsComponent, $event: MouseEvent): void {
        $event.preventDefault();
        this.activeTab = tab;
    }

    protected isActiveTab(tab: CmsComponent, idx: number) {
        return tab === this.activeTab || (this.activeTab == null && idx === 0);
    }

    protected getProductCarouselData(tab: CmsComponent): Observable<AimoCMSCarouselComponentData> {
        return tab.typeCode === 'AimoProductCarouselComponent'
            ? of({
                ...tab,
                showAsAccordion: false,
            })
            : undefined;
    }

    protected getBgClass(carousel: AimoCMSTabbedContainerComponent) {
        return 'bg-' + carousel?.backgroundColor?.toLowerCase().replace('_', '-');
    }

    protected toggleAccordion(): void {
        this.accordionOpen = !this.accordionOpen;
    }
}
